import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Brain, 
  Users, 
  Target,
  Zap,
  Globe,
  Shield,
  Heart,
  Search,
  CheckCircle,
  Clock,
  Sparkles,
  Building,
  BarChart,
  MessageSquare,
  Calendar,
  FileText,
  Bot
} from 'lucide-react';

// Hero Section
const HeroSection = () => (
  <section className="relative overflow-hidden bg-background border-b">
    <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
    <div className="relative container px-4 md:px-6 flex flex-col md:flex-row items-center justify-between min-h-[calc(100vh-4rem)] gap-8 py-12">
      <div className="flex-1 text-center md:text-left max-w-2xl">
        <Badge variant="outline" className="mb-4">For Employers</Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
          Build Your Dream Team in{' '}
          <span className="text-primary">Days, Not Months</span>
        </h1>
        <p className="text-xl text-muted-foreground mb-8">
          Whether you're hiring engineers, product managers, or business talent - our AI-powered platform helps you find and hire the best candidates faster.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
          <Button asChild size="lg" className="text-base">
            <Link to="/register">
              Start Hiring Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <Link to="/pricing">
              View Pricing
              <Building className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
      <div className="flex-1 relative max-w-xl">
        <img 
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80" 
          alt="Team collaboration"
          className="rounded-lg shadow-2xl"
        />
      </div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => (
  <section className="py-20 px-4 md:px-6">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          AI-Powered Hiring Platform
        </h2>
        <p className="mt-4 text-muted-foreground md:text-lg">
          Everything you need to streamline your hiring process
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <FeatureCard 
          icon={Brain}
          title="Smart Candidate Matching"
          description="AI-powered candidate matching and screening"
          features={[
            "Skill-based matching",
            "Culture fit analysis",
            "Experience verification"
          ]}
        />
        
        <FeatureCard 
          icon={Calendar}
          title="Interview Management"
          description="Streamlined interview scheduling and feedback"
          features={[
            "Automated scheduling",
            "Team feedback forms",
            "Interview guides"
          ]}
        />

        <FeatureCard
          icon={BarChart}
          title="Analytics & Insights"
          description="Comprehensive hiring analytics and insights"
          features={[
            "Pipeline metrics",
            "Time-to-hire tracking",
            "Source analytics"
          ]}
        />

        <FeatureCard
          icon={Users}
          title="Team Collaboration"
          description="Tools for effective team hiring"
          features={[
            "Shared pipelines",
            "Team feedback",
            "Hiring analytics"
          ]}
        />

        <FeatureCard
          icon={Shield}
          title="Compliance & Security"
          description="Enterprise-grade security and compliance"
          features={[
            "SOC 2 compliance",
            "Data encryption",
            "GDPR compliance"
          ]}
        />

        <FeatureCard
          icon={Bot}
          title="AI Assistant"
          description="AI-powered hiring assistance"
          features={[
            "Job description writing",
            "Candidate screening",
            "Interview questions"
          ]}
        />
      </div>
    </div>
  </section>
);

// Feature Card Component
const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description, 
  features 
}: { 
  icon: any;
  title: string;
  description: string;
  features: string[];
}) => (
  <Card>
    <CardContent className="p-6">
      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

// Process Section
const ProcessSection = () => (
  <section className="py-20 px-4 md:px-6 bg-muted/50">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          How It Works
        </h2>
        <p className="mt-4 text-muted-foreground md:text-lg">
          A complete hiring solution that combines AI precision with human expertise
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <Card>
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Define Your Needs</h3>
            <p className="text-muted-foreground">
              Specify your requirements and let our AI understand your ideal candidate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Brain className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">AI Matching</h3>
            <p className="text-muted-foreground">
              Our AI finds and ranks the best candidates from our talent pool
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Hire Better</h3>
            <p className="text-muted-foreground">
              Interview and hire candidates who truly match your needs
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// ROI Section
const ROISection = () => (
  <section className="py-20 px-4 md:px-6">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          The NewLoop Advantage
        </h2>
        <p className="mt-4 text-muted-foreground md:text-lg">
          See how our platform transforms your hiring process
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 mx-auto">
              <Clock className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-2">50%</h3>
            <p className="text-muted-foreground">Faster Time to Hire</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 mx-auto">
              <Building className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-2">30%</h3>
            <p className="text-muted-foreground">Lower Hiring Costs</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 mx-auto">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-2">90%</h3>
            <p className="text-muted-foreground">Candidate Satisfaction</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 text-center">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 mx-auto">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-bold mb-2">95%</h3>
            <p className="text-muted-foreground">Hire Success Rate</p>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-20 px-4 md:px-6 bg-primary text-primary-foreground">
    <div className="container">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          Ready to Transform Your Hiring?
        </h2>
        <p className="mt-4 text-primary-foreground/80 md:text-xl">
          Join leading companies using NewLoop to build amazing teams
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            variant="secondary" 
            className="text-base"
            asChild
          >
            <Link to="/register">
              Start Hiring Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="bg-transparent border-white hover:bg-white/10 text-base"
            asChild
          >
            <Link to="/contact">
              Book a Demo
            </Link>
          </Button>
        </div>
      </div>
    </div>
  </section>
);

// Main Marketing Page Component
export default function EmployersMarketingPage() {
  return (
    <div className="flex flex-col w-full">
      <HeroSection />
      <FeaturesSection />
      <ProcessSection />
      <ROISection />
      <CTASection />
    </div>
  );
}